import React, { useEffect, useState, useCallback } from "react";
import {
  Card, CardBody, Col, Container, Row,
  Badge, Button, Input, Spinner, Modal,
  ModalHeader, ModalBody, ModalFooter, Alert,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

const STATUS_ALL      = "all";
const STATUS_UNPAID   = "unpaid";
const STATUS_PAID     = "paid";

const BorcYonetimi = () => {
  document.title = "Borç Yönetimi | LojGo";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const adminId  = authUser?.ID;
  const isAdmin  = authUser?.Users_Type_ID === 3;

  const [rows,        setRows]        = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [actionId,    setActionId]    = useState(null); // hangi kaydın işlemi devam ediyor
  const [statusTab,   setStatusTab]   = useState(STATUS_UNPAID);
  const [search,      setSearch]      = useState("");
  const [alert,       setAlert]       = useState(null); // { type, msg }
  const [confirmRow,  setConfirmRow]  = useState(null); // onay modalı
  const [paytrRow,    setPaytrRow]    = useState(null); // PayTR ödeme modalı
  const [paytrToken,  setPaytrToken]  = useState(null);
  const [paytrLoading, setPaytrLoading] = useState(false);
  const [paytrDone,   setPaytrDone]   = useState(false);

  // ─── VERİ ÇEKME ───────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. İptal politikası ID'lerini al
      const polRes  = await fetch(`${BASE_URL}/Advert_Pricing?$filter=Is_Cancel_Policy eq true`);
      const polData = await polRes.json();
      const cancelIds = (polData.value || []).map(p => p.ID);

      if (cancelIds.length === 0) { setRows([]); setLoading(false); return; }

      // 2. Bu ID'lere ait Users_Advert_Pricing kayıtlarını çek (Users + Advert expand)
      const idFilter = cancelIds.map(id => `Advert_Pricing_ID eq ${id}`).join(" or ");
      // Admin değilse sadece kendi kayıtları
      const userFilter = isAdmin
        ? `(${idFilter})`
        : `(${idFilter}) and Users_ID eq ${adminId}`;
      const rec = await fetch(
        `${BASE_URL}/Users_Advert_Pricing?$filter=${userFilter}&$expand=Users,Advert,Advert_Pricing&$orderby=ID desc`
      );
      const recData = await rec.json();

      const mapped = (recData.value || []).map(r => ({
        id:           r.ID,
        userId:       r.Users_ID,
        userName:     r.Users
          ? (r.Users.Users_NameSurname ||
             `${r.Users.Users_Name_Text || r.Users.Users_Name || ""} ${r.Users.Users_Surname_Text || r.Users.Users_Surname || ""}`.trim() ||
             r.Users.Users_Email_Text || r.Users.Users_Email || "-")
          : "-",
        userPhone:    r.Users?.Users_PhoneNumber || r.Users?.Users_TelephoneNumber || r.Users?.Users_Phone || "-",
        userType:     r.Users?.Users_Type_ID,
        advertId:     r.Advert_ID,
        pricingTitle: r.Advert_Pricing?.Advert_Pricing_Title || "-",
        amount:       r.Price || r.Advert_Pricing?.Price || 0,
        userEmail:    r.Users?.Users_Email_Text || r.Users?.Users_Email || `user${r.Users_ID}@lojgo.com`,
        isPaid:       !!r.Is_Paid,
        paidDate:     r.Paid_DateTime ? new Date(r.Paid_DateTime).toLocaleString("tr-TR") : null,
        createdAt:    r.Advert?.Created_DateTime
                        ? new Date(r.Advert.Created_DateTime).toLocaleDateString("tr-TR")
                        : "-",
      }));

      setRows(mapped);
    } catch (e) {
      showAlert("danger", "Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── FİLTRELEME ───────────────────────────────────────────────
  useEffect(() => {
    let list = rows;
    if (statusTab === STATUS_UNPAID) list = list.filter(r => !r.isPaid);
    if (statusTab === STATUS_PAID)   list = list.filter(r =>  r.isPaid);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.userName.toLowerCase().includes(q) ||
        r.userPhone.includes(q) ||
        String(r.advertId).includes(q)
      );
    }
    setFiltered(list);
  }, [rows, statusTab, search]);

  // ─── YARDIMCI ─────────────────────────────────────────────────
  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  // ─── ÖDEME ONAYLAMA ───────────────────────────────────────────
  const handleMarkPaid = async (row) => {
    setActionId(row.id);
    try {
      const res = await fetch(`${BASE_URL}/Users_Advert_Pricing(${row.id})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Is_Paid:          true,
          Paid_DateTime:    new Date().toISOString(),
          Paid_By_Admin_ID: adminId,
        }),
      });
      if (!res.ok) throw new Error();
      setRows(prev =>
        prev.map(r => r.id === row.id
          ? { ...r, isPaid: true, paidDate: new Date().toLocaleString("tr-TR") }
          : r
        )
      );
      showAlert("success", `${row.userName} kullanıcısının ${row.amount} TL borcu ödendi olarak işaretlendi.`);
    } catch {
      showAlert("danger", "İşlem sırasında hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setActionId(null);
      setConfirmRow(null);
    }
  };

  // ─── PAYTR ÖDEME ──────────────────────────────────────────────
  const handlePayTR = async (row) => {
    setPaytrRow(row);
    setPaytrToken(null);
    setPaytrDone(false);
    setPaytrLoading(true);
    try {
      // Kullanıcı IP'sini al
      let userIp = "0.0.0.0";
      try {
        const ipRes = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipRes.json();
        userIp = ipData.ip || "0.0.0.0";
      } catch { /* IP alınamazsa varsayılanla devam et */ }

      const res = await fetch(`${BASE_URL}/api/Payment/GetPayTRToken`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          Users_ID: row.userId,
          Price:    parseFloat((row.amount || 0).toFixed(2)),
          UserIp:   String(userIp),
        }),
      });

      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }

      if (data.status === "success" && data.token) {
        setPaytrToken(data.token);
      } else {
        throw new Error(data.message || data.reason || "PayTR token alınamadı.");
      }
    } catch (e) {
      showAlert("danger", `PayTR Hatası: ${e.message}`);
      setPaytrRow(null);
    } finally {
      setPaytrLoading(false);
    }
  };

  // PayTR iframe'den gelen mesajları dinle
  useEffect(() => {
    const onMsg = async (e) => {
      if (!paytrRow) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;

        if (d?.status === "success") {
          setPaytrDone(true);
          // DB'de Is_Paid = true yap
          try {
            await fetch(`${BASE_URL}/Users_Advert_Pricing(${paytrRow.id})`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                Is_Paid:       true,
                Paid_DateTime: new Date().toISOString(),
              }),
            });
          } catch {
            // DB güncellemesi başarısız olursa en azından local state güncelle
          }
          // Local state güncelle
          setRows(prev =>
            prev.map(r => r.id === paytrRow.id
              ? { ...r, isPaid: true, paidDate: new Date().toLocaleString("tr-TR") }
              : r
            )
          );
          showAlert("success", "Ödeme PayTR ile başarıyla alındı ve borç kapatıldı.");
          setTimeout(() => { setPaytrRow(null); setPaytrToken(null); setPaytrDone(false); }, 2500);
        }

        if (d?.status === "fail") {
          setPaytrRow(null);
          setPaytrToken(null);
          setPaytrDone(false);
          showAlert("danger", "Ödeme başarısız veya iptal edildi. Lütfen tekrar deneyin.");
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [paytrRow]);

  // ─── İSTATİSTİKLER ────────────────────────────────────────────
  const totalUnpaid  = rows.filter(r => !r.isPaid).reduce((s, r) => s + r.amount, 0);
  const countUnpaid  = rows.filter(r => !r.isPaid).length;
  const totalPaid    = rows.filter(r =>  r.isPaid).reduce((s, r) => s + r.amount, 0);
  const countPaid    = rows.filter(r =>  r.isPaid).length;

  const userTypeLabel = (type) => {
    if (type === 1) return <Badge color="info"  pill>Müşteri</Badge>;
    if (type === 2) return <Badge color="primary" pill>Sürücü</Badge>;
    if (type === 3) return <Badge color="dark"  pill>Admin</Badge>;
    return <Badge color="secondary" pill>-</Badge>;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Borç Yönetimi" />

          {alert && (
            <Alert color={alert.type} className="d-flex align-items-center gap-2">
              <i className={`bx ${alert.type === "success" ? "bx-check-circle" : "bx-error-circle"} fs-5`} />
              {alert.msg}
            </Alert>
          )}

          {/* ── İstatistik Kartları ───────────────────────────── */}
          <Row className="mb-4">
            <Col md={3}>
              <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 20px rgba(220,53,69,0.12)" }}>
                <CardBody className="d-flex align-items-center gap-3">
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: "linear-gradient(135deg, #dc3545, #ff6b6b)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <i className="bx bx-error-circle text-white" style={{ fontSize: 24 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#dc3545" }}>
                      {totalUnpaid.toLocaleString("tr-TR")} TL
                    </div>
                    <div style={{ fontSize: 12, color: "#6c757d" }}>
                      {countUnpaid} ödenmemiş borç
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 20px rgba(25,135,84,0.12)" }}>
                <CardBody className="d-flex align-items-center gap-3">
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: "linear-gradient(135deg, #198754, #2dce89)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <i className="bx bx-check-circle text-white" style={{ fontSize: 24 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#198754" }}>
                      {totalPaid.toLocaleString("tr-TR")} TL
                    </div>
                    <div style={{ fontSize: 12, color: "#6c757d" }}>
                      {countPaid} ödendi
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 20px rgba(85,110,230,0.10)" }}>
                <CardBody className="d-flex align-items-center gap-3">
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: "linear-gradient(135deg, #556ee6, #6f42c1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <i className="bx bx-list-ul text-white" style={{ fontSize: 24 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#556ee6" }}>
                      {rows.length}
                    </div>
                    <div style={{ fontSize: 12, color: "#6c757d" }}>
                      toplam kayıt
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 20px rgba(255,153,0,0.12)" }}>
                <CardBody className="d-flex align-items-center gap-3">
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: "linear-gradient(135deg, #fd7e14, #ffc107)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <i className={`bx ${isAdmin ? "bx-user-x" : "bx-receipt"} text-white`} style={{ fontSize: 24 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#fd7e14" }}>
                      {isAdmin
                        ? new Set(rows.filter(r => !r.isPaid).map(r => r.userId)).size
                        : rows.length}
                    </div>
                    <div style={{ fontSize: 12, color: "#6c757d" }}>
                      {isAdmin ? "borçlu kullanıcı" : "toplam cezam"}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ── Tablo Kartı ──────────────────────────────────── */}
          <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ height: 4, borderRadius: "16px 16px 0 0", background: "linear-gradient(90deg, #556ee6, #6f42c1, #e83e8c)" }} />
            <CardBody>

              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                {/* Tab butonları */}
                <div className="d-flex gap-2">
                  {[
                    { key: STATUS_UNPAID, label: "Ödenmemiş",  color: "#dc3545" },
                    { key: STATUS_PAID,   label: "Ödendi",     color: "#198754" },
                    { key: STATUS_ALL,    label: "Tümü",       color: "#556ee6" },
                  ].map(t => (
                    <button
                      key={t.key}
                      onClick={() => setStatusTab(t.key)}
                      style={{
                        padding: "7px 18px",
                        borderRadius: 10,
                        border: statusTab === t.key ? `2px solid ${t.color}` : "2px solid #e9ecef",
                        background: statusTab === t.key ? t.color : "#fff",
                        color: statusTab === t.key ? "#fff" : "#6c757d",
                        fontWeight: 600, fontSize: 13, cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {t.label}
                      {t.key === STATUS_UNPAID && countUnpaid > 0 && (
                        <span style={{
                          marginLeft: 6, background: statusTab === t.key ? "rgba(255,255,255,0.3)" : "#dc3545",
                          color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 11
                        }}>
                          {countUnpaid}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Arama */}
                <div style={{ position: "relative", minWidth: 260 }}>
                  <i className="bx bx-search" style={{
                    position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                    color: "#adb5bd", fontSize: 17
                  }} />
                  <Input
                    placeholder={isAdmin ? "Ad, telefon veya ilan no ara..." : "İlan no ara..."}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ paddingLeft: 36, borderRadius: 10, border: "1.5px solid #dee2e6" }}
                  />
                </div>

                <button
                  onClick={loadData}
                  style={{
                    padding: "7px 16px", borderRadius: 10,
                    background: "#f8f9ff", border: "1.5px solid #dee2e6",
                    color: "#556ee6", fontWeight: 600, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <i className="bx bx-refresh" style={{ fontSize: 17 }} />
                  Yenile
                </button>
              </div>

              {/* Tablo */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bx bx-check-shield" style={{ fontSize: 48, color: "#2dce89" }} />
                  <p className="mt-2 text-muted fw-semibold">
                    {statusTab === STATUS_UNPAID ? "Ödenmemiş borç bulunmuyor." : "Kayıt bulunamadı."}
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-hover align-middle mb-0" style={{ fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: "#f8f9ff" }}>
                        <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>#</th>
                        {isAdmin && <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>Kullanıcı</th>}
                        {isAdmin && <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>Tür</th>}
                        <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>Ceza Türü</th>
                        <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>İlan</th>
                        <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>Tutar</th>
                        <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>Durum</th>
                        <th style={{ fontWeight: 700, color: "#344050", padding: "12px 16px", border: "none" }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, idx) => (
                        <tr key={row.id} style={{ borderBottom: "1px solid #f1f3fa" }}>
                          <td style={{ padding: "12px 16px", color: "#adb5bd", fontWeight: 600 }}>
                            {idx + 1}
                          </td>
                          {isAdmin && (
                            <td style={{ padding: "12px 16px" }}>
                              <div style={{ fontWeight: 700, color: "#344050" }}>{row.userName}</div>
                              <div style={{ fontSize: 12, color: "#8592c0" }}>
                                <i className="bx bx-phone me-1" />
                                {row.userPhone}
                              </div>
                            </td>
                          )}
                          {isAdmin && (
                            <td style={{ padding: "12px 16px" }}>
                              {userTypeLabel(row.userType)}
                            </td>
                          )}
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              background: "#fff3cd", color: "#856404",
                              borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600
                            }}>
                              <i className="bx bx-x-circle me-1" />
                              {row.pricingTitle}
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <a
                              href={`/job-details/${row.advertId}`}
                              style={{ color: "#556ee6", fontWeight: 600, textDecoration: "none" }}
                            >
                              <i className="bx bx-link-external me-1" />
                              #{row.advertId}
                            </a>
                            <div style={{ fontSize: 11, color: "#adb5bd" }}>{row.createdAt}</div>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              fontSize: 15, fontWeight: 800,
                              color: row.isPaid ? "#198754" : "#dc3545",
                            }}>
                              {row.amount.toLocaleString("tr-TR")} TL
                            </span>
                          </td>
                          {/* Durum */}
                          <td style={{ padding: "12px 16px" }}>
                            {row.isPaid ? (
                              <div>
                                <Badge color="success" style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12 }}>
                                  <i className="bx bx-check me-1" />Ödendi
                                </Badge>
                                {row.paidDate && (
                                  <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 3 }}>
                                    {row.paidDate}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Badge color="danger" style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12 }}>
                                <i className="bx bx-time me-1" />Ödenmedi
                              </Badge>
                            )}
                          </td>
                          {/* İşlem */}
                          <td style={{ padding: "12px 16px" }}>
                            {!row.isPaid && (
                              <div className="d-flex flex-column gap-2">
                                <button
                                  onClick={() => handlePayTR(row)}
                                  disabled={actionId === row.id}
                                  style={{
                                    padding: "7px 14px",
                                    background: "linear-gradient(135deg, #e84393, #6f42c1)",
                                    border: "none", borderRadius: 8,
                                    color: "#fff", fontWeight: 600, fontSize: 12,
                                    cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 5,
                                    boxShadow: "0 4px 12px rgba(232,67,147,0.3)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <i className="bx bx-credit-card" />PayTR ile Öde
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={() => setConfirmRow(row)}
                                    disabled={actionId === row.id}
                                    style={{
                                      padding: "6px 14px",
                                      background: "linear-gradient(135deg, #198754, #2dce89)",
                                      border: "none", borderRadius: 8,
                                      color: "#fff", fontWeight: 600, fontSize: 12,
                                      cursor: "pointer",
                                      display: "flex", alignItems: "center", gap: 5,
                                      boxShadow: "0 4px 12px rgba(25,135,84,0.3)",
                                      opacity: actionId === row.id ? 0.7 : 1,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {actionId === row.id
                                      ? <><Spinner size="sm" /> İşleniyor...</>
                                      : <><i className="bx bx-check-circle" />Manuel Ödendi</>
                                    }
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Alt bilgi */}
              {!loading && filtered.length > 0 && (
                <div className="text-muted mt-3" style={{ fontSize: 13 }}>
                  Toplam <strong>{filtered.length}</strong> kayıt gösteriliyor.
                  {statusTab === STATUS_UNPAID && (
                    <span className="ms-2 text-danger fw-semibold">
                      Toplam ödenmemiş: {filtered.reduce((s, r) => s + r.amount, 0).toLocaleString("tr-TR")} TL
                    </span>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* ── Onay Modalı ─────────────────────────────────────── */}
      <Modal isOpen={!!confirmRow} toggle={() => setConfirmRow(null)} centered>
        <ModalHeader
          toggle={() => setConfirmRow(null)}
          style={{ borderBottom: "none", paddingBottom: 0 }}
        >
          <div className="d-flex align-items-center gap-2">
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, #198754, #2dce89)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bx bx-check-circle text-white" style={{ fontSize: 18 }} />
            </div>
            <span style={{ fontWeight: 700 }}>Ödeme Onayı</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {confirmRow && (
            <div>
              <div style={{
                background: "#f8f9ff", borderRadius: 12, padding: 16, marginBottom: 16,
                border: "1px solid #e8ecff"
              }}>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: "#6c757d", fontSize: 13 }}>Kullanıcı</span>
                  <span style={{ fontWeight: 700 }}>{confirmRow.userName}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: "#6c757d", fontSize: 13 }}>Telefon</span>
                  <span style={{ fontWeight: 600 }}>{confirmRow.userPhone}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: "#6c757d", fontSize: 13 }}>Ceza Türü</span>
                  <span style={{ fontWeight: 600 }}>{confirmRow.pricingTitle}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span style={{ color: "#6c757d", fontSize: 13 }}>Tutar</span>
                  <span style={{ fontWeight: 800, fontSize: 18, color: "#198754" }}>
                    {confirmRow.amount.toLocaleString("tr-TR")} TL
                  </span>
                </div>
              </div>
              <p style={{ fontSize: 14, color: "#495057", margin: 0 }}>
                Bu borcu <strong>ödendi</strong> olarak işaretlemek istediğinizden emin misiniz?
                <br />
                <small className="text-muted">
                  İşlem geri alınamaz. Kullanıcının borç engeli kalkacaktır.
                </small>
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: "none" }}>
          <Button color="light" onClick={() => setConfirmRow(null)} style={{ borderRadius: 8 }}>
            İptal
          </Button>
          <button
            onClick={() => handleMarkPaid(confirmRow)}
            disabled={actionId === confirmRow?.id}
            style={{
              padding: "8px 20px",
              background: "linear-gradient(135deg, #198754, #2dce89)",
              border: "none", borderRadius: 8, color: "#fff",
              fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {actionId === confirmRow?.id ? (
              <><Spinner size="sm" />İşleniyor...</>
            ) : (
              <><i className="bx bx-check-circle" />Evet, Ödendi İşaretle</>
            )}
          </button>
        </ModalFooter>
      </Modal>
      {/* ── PayTR Ödeme Modalı ───────────────────────────────── */}
      <Modal
        isOpen={!!paytrRow}
        toggle={() => { setPaytrRow(null); setPaytrToken(null); }}
        centered
        size="lg"
        scrollable
      >
        <ModalHeader
          toggle={() => { setPaytrRow(null); setPaytrToken(null); }}
          style={{ borderBottom: "none", paddingBottom: 0 }}
        >
          <div className="d-flex align-items-center gap-2">
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, #e84393, #6f42c1)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bx bx-credit-card text-white" style={{ fontSize: 18 }} />
            </div>
            <span style={{ fontWeight: 700 }}>PayTR ile Ödeme Al</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {paytrRow && (
            <div>
              {/* Borç özeti */}
              <div style={{ background: "#f8f9ff", padding: "16px 24px", borderBottom: "1px solid #e8ecff" }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#344050" }}>
                      {paytrRow.userName}
                    </div>
                    <div style={{ fontSize: 12, color: "#8592c0" }}>
                      <i className="bx bx-phone me-1" />{paytrRow.userPhone}
                      <span className="ms-3"><i className="bx bx-x-circle me-1" />{paytrRow.pricingTitle}</span>
                    </div>
                  </div>
                  <div style={{
                    fontSize: 22, fontWeight: 900,
                    background: "linear-gradient(135deg, #e84393, #6f42c1)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  }}>
                    {(paytrRow.amount || 0).toLocaleString("tr-TR")} TL
                  </div>
                </div>
              </div>

              {/* PayTR iframe veya loading */}
              <div style={{ minHeight: 480, position: "relative" }}>
                {paytrLoading && (
                  <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 480 }}>
                    <Spinner color="primary" style={{ width: 40, height: 40 }} />
                    <p className="mt-3 text-muted fw-semibold">PayTR ödeme sayfası yükleniyor...</p>
                  </div>
                )}

                {paytrDone && (
                  <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 480 }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: "50%",
                      background: "linear-gradient(135deg, #198754, #2dce89)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      marginBottom: 16,
                    }}>
                      <i className="bx bx-check text-white" style={{ fontSize: 40 }} />
                    </div>
                    <h5 style={{ fontWeight: 700, color: "#198754" }}>Ödeme Başarılı!</h5>
                    <p className="text-muted">Borç ödendi olarak işaretlendi.</p>
                  </div>
                )}

                {!paytrLoading && !paytrDone && paytrToken && (
                  <iframe
                    src={`https://www.paytr.com/odeme/guvenli/${paytrToken}`}
                    id="paytriframe"
                    title="PayTR Ödeme"
                    frameBorder="0"
                    scrolling="yes"
                    sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-modals"
                    style={{ width: "100%", height: 600, border: "none", display: "block" }}
                  />
                )}

                {!paytrLoading && !paytrDone && !paytrToken && (
                  <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 480 }}>
                    <i className="bx bx-error-circle text-danger" style={{ fontSize: 48 }} />
                    <p className="mt-2 text-muted">Ödeme sayfası yüklenemedi.</p>
                    <button
                      onClick={() => handlePayTR(paytrRow)}
                      style={{
                        padding: "8px 20px", borderRadius: 8,
                        background: "linear-gradient(135deg, #e84393, #6f42c1)",
                        border: "none", color: "#fff", fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      <i className="bx bx-refresh me-1" />Tekrar Dene
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #f1f3fa" }}>
          <div style={{ fontSize: 12, color: "#adb5bd", flex: 1 }}>
            <i className="bx bx-lock me-1" />
            SSL ile güvenli ödeme — PayTR
          </div>
          <Button
            color="light"
            onClick={() => { setPaytrRow(null); setPaytrToken(null); }}
            style={{ borderRadius: 8 }}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default BorcYonetimi;
